import React from 'react'
import { useOrgStore } from '../../stores/orgStore'
import { Select } from '../ui/Select/Select'
import { useTenantMode } from '@/hooks/useTenantMode'

/**
 * Org switcher shown in the top-right header for every user that belongs to
 * at least one organisation.
 *
 * - 0 orgs  → renders nothing (existing behaviour preserved).
 * - 1 org   → renders the Select with that single org pre-selected (AC-Global-1).
 *             The dropdown is interactive but contains only one option.
 * - 2+ orgs → renders the Select with all options; switching triggers a page
 *             reload so every cache/state is flushed cleanly.
 *
 * Tenant mode (AC-PROD-1): when running on a tenant subdomain (e.g. acme.roros.ai),
 *   the org switcher is locked to the host org — only that org is shown and
 *   switching is disabled (the user is bound to the tenant).
 *
 * The static "Org" label is intentionally removed for all cases (AC-Global-1).
 *
 * Switching is purely client-side: the JWT is org-agnostic, the active org
 * lives in localStorage (orgStore), and every API call sends the active org
 * via the X-Organization-Id header.
 */
export function OrganizationSwitcher() {
  const orgs = useOrgStore(s => s.orgs)
  const activeOrgId = useOrgStore(s => s.activeOrgId)
  const setActiveOrgId = useOrgStore(s => s.setActiveOrgId)
  const { lockOrgSwitcher } = useTenantMode()

  // Zero-org users: render nothing (existing behaviour preserved).
  if (orgs.length === 0) return null

  // On org switch, hard-reload the page. This is the standard pattern in
  // multi-tenant SaaS (Linear / Notion / Figma do the same): switching tenants
  // resets every cached query, every form-local state, every WebSocket
  // subscription. Far simpler + safer than orchestrating a full TanStack
  // invalidation cascade + per-component state resets.
  const handleSwitch = (newOrgId: string) => {
    if (newOrgId === activeOrgId) return
    setActiveOrgId(newOrgId)
    // Navigate to the home route on reload to avoid landing on a page that
    // referenced an entity from the previous org (e.g. /roros/decisions/<id>
    // where <id> belongs to the old tenant).
    window.location.href = '/'
  }

  // Tenant mode: show only the active org, disable switching (AC-PROD-1)
  if (lockOrgSwitcher) {
    const activeOrg = orgs.find(o => o.id === activeOrgId) || orgs[0]
    if (!activeOrg) return null
    return (
      <div className="flex items-center gap-2" data-testid="org-switcher" data-tenant-locked="true">
        <span className="text-sm font-medium text-gray-700 px-3 py-1 bg-gray-100 rounded-md">
          {activeOrg.name}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2" data-testid="org-switcher">
      <Select
        value={activeOrgId || ''}
        onValueChange={(v) => handleSwitch(String(v))}
        options={orgs.map(o => ({ label: o.name, value: o.id }))}
      />
    </div>
  )
}
